import { isUntimedSlot } from '@/components/flows/detail/slot-list/slot-timing';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

function getSessionContentId(slot: FlowSlotDto): string | undefined {
  if (slot.contentType !== 'Session' || !slot.content) return undefined;
  if (typeof slot.content === 'string') return slot.content;
  if (typeof slot.content === 'object' && slot.content && 'id' in slot.content) {
    return (slot.content as { id: string }).id;
  }
  return undefined;
}

/**
 * After slots are replaced (e.g. optimistic temp id → server ObjectId), find the same row by
 * slot id first, then session content id, then break timing/title. Avoids clearing selection and
 * triggering first-slot fallback effects.
 */
export function findUpdatedSlotForSelection(
  slots: FlowSlotDto[] | undefined,
  selected: FlowSlotDto,
): FlowSlotDto | undefined {
  if (!slots?.length) return undefined;

  const byId = slots.find((s) => s.id === selected.id);
  if (byId) return byId;

  const sessionId = getSessionContentId(selected);
  if (sessionId) {
    const bySession = slots.find((s) => {
      if (s.contentType !== 'Session') return false;
      return getSessionContentId(s) === sessionId;
    });
    if (bySession) return bySession;
  }

  if (selected.contentType === 'Break') {
    return slots.find(
      (s) =>
        s.contentType === 'Break' &&
        s.title === selected.title &&
        isUntimedSlot(s) === isUntimedSlot(selected) &&
        (s.computedOffsetMinutes ?? null) === (selected.computedOffsetMinutes ?? null) &&
        (s.computedDurationMinutes ?? null) === (selected.computedDurationMinutes ?? null),
    );
  }

  return undefined;
}
